import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const BOOKING_TIMEZONE = 'Europe/Warsaw';
export const REFRESH_INTERVAL_MS = 30_000;
const warsawFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: BOOKING_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
export type RentalOffer = 'three-hours' | 'day' | 'weekend';
export type AvailabilityStatus = 'loading' | 'available' | 'unavailable' | 'unknown' | 'error';

export interface BookingRange {
  startsAt: string;
  endsAt: string;
}

export interface RentalReturnInput {
  date: string;
  time: string;
}

export interface BookingSelection extends BookingRange {
  offer: RentalOffer;
  vehicleId: string;
  accessoryIds: string[];
  paymentType: 'deposit' | 'full';
}

export interface BookingQuote {
  quoteId: string;
  selectionKey: string;
  expiresAt: string;
  currency: 'PLN';
  rentalMinor: number;
  accessoriesMinor: number;
  totalMinor: number;
  refundableDepositMinor: number;
  payableNowMinor: number;
  rulesVersion: string;
}

export interface RentalConfig {
  timezone: typeof BOOKING_TIMEZONE;
  currency: 'PLN';
  rulesVersion: string;
  bookingEnabled: boolean;
  termsVersion: string | null;
  offers: { id: RentalOffer; enabled: boolean; accessoriesAllowed: boolean }[];
  calendar: {
    openingStart: string; openingEnd: string; minuteStep: number; maxDays: number;
    minLeadMinutes: number; sameDayCutoff: string;
    vehicleBufferMinutes: number; accessoryBufferMinutes: number;
  };
  advancePayment: { percent: number; roundingMinor: number; cappedAtTotal: boolean };
  holdMinutes: number;
  availabilityTtlSeconds: number;
  quoteTtlSeconds: number;
  paymentProvider: 'imoje';
}

export interface AvailabilitySnapshot {
  checkedAt: string;
  expiresAt: string;
  vehicles: { id: string; status: 'available' | 'unavailable' | 'unknown' }[];
  accessories: { id: string; status: 'available' | 'unavailable' | 'unknown' }[];
}

export interface Customer {
  name: string;
  email: string;
  phone: string;
}

export interface ReservationPayload extends Omit<BookingSelection, 'offer'> {
  offerId: RentalOffer;
  quoteId: string;
  customer: Customer;
  message: string;
  termsAccepted: boolean;
  rodoAccepted: boolean;
  depositAccepted: boolean;
  termsVersion: string;
  marketingConsent: boolean;
  locale: 'pl' | 'en' | 'de';
}

export interface ReservationCredentials {
  reservationId: string;
  statusToken: string;
}

export interface ReservationStatus extends Pick<BookingQuote,
  'currency' | 'rentalMinor' | 'accessoriesMinor' | 'totalMinor' | 'refundableDepositMinor' | 'payableNowMinor'> {
  reservationId: string;
  status: string;
  paymentProvider: 'imoje';
  paymentState: string;
  holdExpiresAt: string;
  redirectUri: string | null;
}

export interface ReservationAttempt {
  key: string;
  payload: ReservationPayload;
  createdAt: number;
}

export type RangeResult =
  | { status: 'ready'; range: BookingRange }
  | { status: 'invalid'; code: 'INVALID_DATE' | 'DST_TIME' | 'OUTSIDE_HOURS' | 'PAST' | 'TOO_FAR' | 'TOO_SOON' | 'TODAY_CLOSED' | 'INVALID_STEP' | 'CALENDAR_CONFIG' | 'RETURN_BEFORE_PICKUP' | 'RENTAL_TOO_SHORT' | 'INVALID_WEEKEND' }
  | { status: 'blocked'; code: 'OFFER_RULES_MISSING' };

export interface BusySlot {
  fromDate: string;
  fromHour: string;
  toDate: string;
  toHour: string;
}

export interface BusySnapshot {
  global: BusySlot[];
  byModel: Record<string, BusySlot[]>;
}

export function getDefaultReturn(offer: RentalOffer, date: string, time: string): RentalReturnInput | null {
  if (offer === 'weekend') return null;
  const start = parseWarsawTime(date, time);
  if (!start) return null;
  const minutes = offer === 'day' ? 1440 : 180;
  const end = dayjs(start.valueOf() + minutes * 60_000).tz(BOOKING_TIMEZONE);
  return { date: end.format('YYYY-MM-DD'), time: end.format('HH:mm') };
}

export function getTimeOptions(start: string, end: string): string[] {
  return Array.from({ length: 48 }, (_, index) => {
    const minutes = index * 30;
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }).filter(time => time >= start && time <= end);
}

export function getReturnAfterPickupChange(date: string, time: string, chosenReturn: RentalReturnInput | null) {
  if (!chosenReturn) return null;
  const start = parseWarsawTime(date, time);
  const end = parseWarsawTime(chosenReturn.date, chosenReturn.time);
  if (!start || !end) return chosenReturn;
  return end.valueOf() - start.valueOf() >= 180 * 60_000 ? chosenReturn : null;
}

// Legacy calendar limits are only a UI constraint, not server confirmation.
export function getBookingRange(
  offer: RentalOffer,
  date: string,
  time: string,
  limits: { start: string; end: string; maxDays: number; minLeadMinutes?: number; stepMinutes?: number; lastTodayHour?: number; sameDayCutoff?: string; exactHorizon?: boolean },
  now = Date.now(),
  plannedReturn?: RentalReturnInput,
): RangeResult {
  if (offer === 'weekend') return { status: 'blocked', code: 'OFFER_RULES_MISSING' };
  if (!dayjs(`${date}T${time}`, 'YYYY-MM-DDTHH:mm', true).isValid()) return { status: 'invalid', code: 'INVALID_DATE' };
  const start = parseWarsawTime(date, time);
  if (!start) return { status: 'invalid', code: 'DST_TIME' };
  if (!Number.isFinite(limits.maxDays) || limits.maxDays < 0
    || (limits.minLeadMinutes !== undefined && (!Number.isFinite(limits.minLeadMinutes) || limits.minLeadMinutes < 0))
    || (limits.stepMinutes !== undefined && (!Number.isInteger(limits.stepMinutes) || limits.stepMinutes <= 0 || limits.stepMinutes > 60 || 60 % limits.stepMinutes !== 0))
    || (limits.lastTodayHour !== undefined && (!Number.isFinite(limits.lastTodayHour) || limits.lastTodayHour < 0 || limits.lastTodayHour > 23))
    || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(limits.start)
    || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(limits.end)) {
    return { status: 'invalid', code: 'CALENDAR_CONFIG' };
  }
  if (limits.stepMinutes && start.minute() % limits.stepMinutes !== 0) return { status: 'invalid', code: 'INVALID_STEP' };
  const durationMinutes = offer === 'day' ? 1440 : 180;
  if (offer === 'day' && plannedReturn && !dayjs(`${plannedReturn.date}T${plannedReturn.time}`, 'YYYY-MM-DDTHH:mm', true).isValid()) {
    return { status: 'invalid', code: 'INVALID_DATE' };
  }
  const end = offer === 'day' && plannedReturn
    ? parseWarsawTime(plannedReturn.date, plannedReturn.time)
    : dayjs(start.valueOf() + durationMinutes * 60_000).tz(BOOKING_TIMEZONE);
  if (!end) return { status: 'invalid', code: 'DST_TIME' };
  if (limits.stepMinutes && end.minute() % limits.stepMinutes !== 0) return { status: 'invalid', code: 'INVALID_STEP' };
  const actualMinutes = (end.valueOf() - start.valueOf()) / 60_000;
  if (actualMinutes <= 0) return { status: 'invalid', code: 'RETURN_BEFORE_PICKUP' };
  if (actualMinutes < 180) return { status: 'invalid', code: 'RENTAL_TOO_SHORT' };
  if (start.valueOf() <= now) return { status: 'invalid', code: 'PAST' };
  if (start.valueOf() < now + (limits.minLeadMinutes || 0) * 60_000) return { status: 'invalid', code: 'TOO_SOON' };
  const today = dayjs(now).tz(BOOKING_TIMEZONE);
  if (date === today.format('YYYY-MM-DD') && limits.lastTodayHour !== undefined && today.hour() >= limits.lastTodayHour) {
    return { status: 'invalid', code: 'TODAY_CLOSED' };
  }
  if (date === today.format('YYYY-MM-DD') && limits.sameDayCutoff && today.format('HH:mm') >= limits.sameDayCutoff) {
    return { status: 'invalid', code: 'TODAY_CLOSED' };
  }
  const lastDate = dayjs(now).tz(BOOKING_TIMEZONE).add(limits.maxDays, 'day').format('YYYY-MM-DD');
  if (date > lastDate || end.format('YYYY-MM-DD') > lastDate) return { status: 'invalid', code: 'TOO_FAR' };
  if (limits.exactHorizon && end.valueOf() > now + limits.maxDays * 86_400_000) {
    return { status: 'invalid', code: 'TOO_FAR' };
  }
  if ((offer === 'three-hours' && date !== end.format('YYYY-MM-DD'))
    || time < limits.start || time > limits.end
    || end.format('HH:mm') < limits.start || end.format('HH:mm') > limits.end) {
    return { status: 'invalid', code: 'OUTSIDE_HOURS' };
  }
  return {
    status: 'ready',
    range: { startsAt: start.toISOString(), endsAt: end.toISOString() },
  };
}

export function parseWarsawTime(date: string, time: string) {
  const local = `${date}T${time}`;
  if (!dayjs(local, 'YYYY-MM-DDTHH:mm', true).isValid()) return null;
  const parsed = dayjs.tz(local, BOOKING_TIMEZONE);
  if (parsed.format('YYYY-MM-DDTHH:mm') !== local) return null;
  // Reject ambiguous autumn times as well as nonexistent spring times.
  for (const offset of [-60, 60]) {
    const parts = warsawFormatter.formatToParts(parsed.valueOf() + offset * 60_000);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(value => value.type === type)?.value;
    const wallTime = `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
    if (wallTime === local) {
      return null;
    }
  }
  return parsed;
}

export function parseBusyTime(date: string, time: string) {
  const isoDate = /^\d{2}-\d{2}-\d{4}$/.test(date)
    ? `${date.slice(6)}-${date.slice(3, 5)}-${date.slice(0, 2)}`
    : date;
  return parseWarsawTime(isoDate, time);
}

export function getSnapshotAvailability(snapshot: BusySnapshot, model: string, range: BookingRange): AvailabilityStatus {
  const start = Date.parse(range.startsAt);
  const end = Date.parse(range.endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 'error';
  for (const slot of [...snapshot.global, ...(snapshot.byModel[model] || [])]) {
    const busyStart = parseBusyTime(slot.fromDate, slot.fromHour);
    const busyEnd = parseBusyTime(slot.toDate, slot.toHour);
    if (!busyStart || !busyEnd || busyEnd.valueOf() <= busyStart.valueOf()) return 'error';
    if (start < busyEnd.valueOf() && end > busyStart.valueOf()) return 'unavailable';
  }
  // A gap in a model-level busy list does not confirm vehicle availability.
  return 'unknown';
}

export function selectionKey(selection: BookingSelection) {
  return JSON.stringify([
    selection.offer, new Date(selection.startsAt).toISOString(), new Date(selection.endsAt).toISOString(), selection.vehicleId,
    [...selection.accessoryIds].sort(), selection.paymentType,
  ]);
}

export function getConfiguredRange(
  offer: RentalOffer, date: string, time: string, config: RentalConfig,
  now: number, plannedReturn?: RentalReturnInput,
): RangeResult {
  if (!config.offers.some(item => item.id === offer && item.enabled)) {
    return { status: 'blocked', code: 'OFFER_RULES_MISSING' };
  }
  const calendar = config.calendar;
  const limits = {
    start: calendar.openingStart, end: calendar.openingEnd, maxDays: calendar.maxDays,
    minLeadMinutes: calendar.minLeadMinutes, stepMinutes: calendar.minuteStep,
    sameDayCutoff: calendar.sameDayCutoff, exactHorizon: true,
  };
  if (offer !== 'weekend') return getBookingRange(offer, date, time, limits, now, plannedReturn);
  const start = parseWarsawTime(date, '09:00');
  if (!start) return { status: 'invalid', code: 'INVALID_DATE' };
  if (start.day() !== 5) return { status: 'invalid', code: 'INVALID_WEEKEND' };
  return getBookingRange('day', date, '09:00', limits, now,
    { date: start.add(2, 'day').format('YYYY-MM-DD'), time: '20:00' });
}

export function getNextBookingSlot(
  offer: RentalOffer, config: RentalConfig, now: number, after = now, durationMinutes?: number,
): { date: string; time: string; range: BookingRange } | null {
  if (!config.offers.some(item => item.id === offer && item.enabled)) return null;
  const today = dayjs(now).tz(BOOKING_TIMEZONE);
  const firstDay = Math.max(0, dayjs(dayjs(after).tz(BOOKING_TIMEZONE).format('YYYY-MM-DD'))
    .diff(dayjs(today.format('YYYY-MM-DD')), 'day'));
  const times = offer === 'weekend' ? ['09:00']
    : getTimeOptions(config.calendar.openingStart, config.calendar.openingEnd);
  for (let day = firstDay; day <= config.calendar.maxDays; day++) {
    const date = today.add(day, 'day').format('YYYY-MM-DD');
    for (const time of times) {
      const start = parseWarsawTime(date, time);
      if (!start || start.valueOf() <= after) continue;
      const end = durationMinutes === undefined ? null
        : dayjs(start.valueOf() + durationMinutes * 60_000).tz(BOOKING_TIMEZONE);
      const plannedReturn = offer === 'day' && end
        ? { date: end.format('YYYY-MM-DD'), time: end.format('HH:mm') } : undefined;
      const result = getConfiguredRange(offer, date, time, config, now, plannedReturn);
      if (result.status === 'ready') return { date, time, range: result.range };
    }
  }
  return null;
}

export function getConfirmedAvailability(
  snapshot: AvailabilitySnapshot, kind: 'vehicles' | 'accessories', id: string, now = Date.now(),
): AvailabilityStatus {
  if (!Number.isFinite(Date.parse(snapshot.expiresAt)) || Date.parse(snapshot.expiresAt) <= now) return 'unknown';
  return snapshot[kind].find(item => item.id === id)?.status ?? 'unknown';
}

export function validateCustomer(customer: Customer): Partial<Record<keyof Customer, string>> {
  const errors: Partial<Record<keyof Customer, string>> = {};
  if (customer.name.trim().length < 2 || customer.name.trim().length > 120) errors.name = 'name';
  if (customer.email.trim().length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) errors.email = 'email';
  const phone = customer.phone.trim();
  if (phone.length < 7 || phone.length > 25 || !/^\+?[\d ()-]+$/.test(phone)
    || phone.replace(/\D/g, '').length < 7) errors.phone = 'phone';
  return errors;
}

export function canOpenPayment(status: ReservationStatus, now = Date.now()) {
  return ['pending', 'payment-pending'].includes(status.status)
    && status.paymentState === 'ready' && Date.parse(status.holdExpiresAt) > now && !!status.redirectUri;
}

export function isFinalReservationStatus(status: ReservationStatus) {
  return ['payment-completed', 'payment-canceled', 'hold-expired', 'payment-review'].includes(status.status)
    || ['unknown', 'failed'].includes(status.paymentState);
}

export function canStartNewReservation(status: ReservationStatus | null) {
  return !!status && ['payment-completed', 'payment-canceled', 'hold-expired'].includes(status.status);
}

export function isUsableQuote(quote: BookingQuote, selection: BookingSelection, now = Date.now()) {
  const amounts = [quote.rentalMinor, quote.accessoriesMinor, quote.totalMinor, quote.refundableDepositMinor, quote.payableNowMinor];
  return quote.currency === 'PLN' && !!quote.quoteId
    && quote.selectionKey === selectionKey(selection)
    && Date.parse(quote.expiresAt) > now
    && amounts.every(value => Number.isSafeInteger(value) && value >= 0)
    && quote.totalMinor === quote.rentalMinor + quote.accessoriesMinor
    && quote.payableNowMinor <= quote.totalMinor;
}

export function formatMoney(minor: number, locale: string) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'PLN' }).format(minor / 100);
}
